import React from "react";
import { Link } from "react-router-dom";
import JTLogo from "../components/JTLogo";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useReveal } from "../hooks/useReveal";

const CHARTER_CITIES = [
  "Atlanta", "Boston", "Detroit", "Houston", "Nashville",
  "New York City", "Philadelphia", "Salt Lake City", "San Francisco",
];

export default function Landing() {
  return (
    <div className="App" data-testid="landing-page">
      <SiteHeader />

      <Hero />
      <TheLounge />
      <CharterClass />
      <VerifiedSection />
      <WhyWeBuiltThis />
      <WhyMembersJoin />
      <ClosingInvitation />

      <SiteFooter />
    </div>
  );
}

/* ------------------------------------ HERO ------------------------------------ */

const Hero = () => {
  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] flex flex-col items-center justify-center px-6 py-24 sm:py-28 lounge-vignette overflow-hidden grain"
    >
      <div className="fade-up flex flex-col items-center text-center max-w-[1080px]">
        <JTLogo size={108} withWordmark />

        <h1
          data-testid="hero-headline"
          className="font-display mt-14"
          style={{
            fontSize: "clamp(2.6rem, 6vw, 5.8rem)",
            lineHeight: 1.02,
            letterSpacing: "-0.015em",
            color: "var(--jt-ivory)",
            fontWeight: 500,
            maxWidth: "18ch",
            textTransform: "uppercase",
          }}
        >
          Stop spending layovers alone.
        </h1>

        <p
          data-testid="hero-subheadline"
          className="font-editorial italic mt-10"
          style={{
            fontSize: "clamp(1.15rem, 1.7vw, 1.6rem)",
            color: "var(--jt-bone)",
            maxWidth: "46ch",
            lineHeight: 1.55,
          }}
        >
          Meet verified flight attendants, discover places worth leaving the hotel for, and experience the city again.
        </p>

        <div className="mt-12 flex items-center justify-center gap-6">
          <div className="gold-divider" />
        </div>

        <div
          data-testid="hero-brand-statement"
          className="font-editorial italic mt-10"
          style={{
            color: "var(--jt-gold)",
            fontSize: "clamp(1.15rem, 1.6vw, 1.5rem)",
            letterSpacing: "0.005em",
            lineHeight: 1.4,
          }}
        >
          Find Familiar Faces In Unfamiliar Cities.
        </div>

        <Link to="/apply" className="jt-cta mt-12" data-testid="hero-cta-request">
          Request Membership
        </Link>
      </div>
    </section>
  );
};

/* ---------------------------------- THE LOUNGE ---------------------------------- */

const TheLounge = () => {
  const [ref, shown] = useReveal();
  return (
    <section
      ref={ref}
      data-testid="the-lounge-section"
      data-shown={shown}
      className="reveal relative py-24 px-6"
    >
      <div className="max-w-[860px] mx-auto text-center">
        <div
          className="font-label"
          style={{ color: "var(--jt-gold)", fontSize: "11px", marginBottom: 28 }}
        >
          THE LOUNGE
        </div>

        <h2
          className="font-display"
          style={{
            fontSize: "clamp(2.2rem, 4.4vw, 3.6rem)",
            color: "var(--jt-ivory)",
            fontWeight: 500,
            lineHeight: 1.05,
            letterSpacing: "-0.01em",
            maxWidth: "16ch",
            margin: "0 auto",
          }}
        >
          <span className="font-editorial italic" style={{ color: "var(--jt-bone)" }}>
            Not a social network.
          </span>
          <br />
          A members club.
        </h2>

        <p
          className="mt-10"
          style={{
            color: "var(--jt-silver)",
            fontSize: "17px",
            lineHeight: 1.7,
            maxWidth: "44ch",
            margin: "40px auto 0",
          }}
        >
          Built for flight attendants who still want to experience the cities they fly to.
        </p>

        {/* Editorial em-dash divider */}
        <div
          aria-hidden="true"
          className="mx-auto"
          style={{
            width: 28,
            height: 1,
            background: "var(--jt-gold)",
            marginTop: 56,
            marginBottom: 56,
            opacity: 0.7,
          }}
        />

        <div
          className="font-label"
          style={{ color: "var(--jt-gold)", fontSize: "12px", letterSpacing: "0.42em" }}
        >
          MEET. &nbsp;·&nbsp; EXPLORE. &nbsp;·&nbsp; CONNECT.
        </div>

        <div
          className="mt-10 font-editorial italic"
          data-testid="lounge-across"
          style={{
            color: "var(--jt-bone)",
            fontSize: "clamp(1.3rem, 2.2vw, 1.75rem)",
            lineHeight: 1.55,
          }}
        >
          <div>Across airlines.</div>
          <div>Across bases.</div>
          <div>Across cities.</div>
        </div>

        <p
          className="mt-12 font-editorial italic"
          style={{
            color: "var(--jt-gold)",
            fontSize: "clamp(1.2rem, 2vw, 1.6rem)",
            lineHeight: 1.5,
            maxWidth: "30ch",
            margin: "48px auto 0",
          }}
        >
          Without spending another layover alone.
        </p>
      </div>
    </section>
  );
};

/* -------------------------------- CHARTER CLASS -------------------------------- */

const CharterClass = () => {
  const [ref, shown] = useReveal();
  return (
    <section
      ref={ref}
      data-testid="charter-section"
      data-shown={shown}
      className="reveal relative py-24 px-6 lounge-soft"
    >
      <div className="max-w-[980px] mx-auto text-center">
        <div
          className="font-label"
          style={{ color: "var(--jt-gold)", fontSize: "11px", marginBottom: 24 }}
        >
          CHARTER CLASS 2026 · 25 OF 25 SEATS FILLED
        </div>

        <h2
          className="font-display"
          style={{
            fontSize: "clamp(1.75rem, 3.2vw, 2.6rem)",
            color: "var(--jt-ivory)",
            fontWeight: 500,
            lineHeight: 1.2,
            letterSpacing: "0.005em",
            maxWidth: "28ch",
            margin: "0 auto",
          }}
        >
          Twenty-five flight attendants. Multiple airlines.
          {" "}<span className="font-editorial italic" style={{ color: "var(--jt-bone)" }}>
            Major bases across the United States.
          </span>
        </h2>

        <p
          className="font-label mt-8"
          style={{ color: "var(--jt-gold)", fontSize: "12px", letterSpacing: "0.32em", lineHeight: 1.7 }}
        >
          THE CHARTER CLASS WAS COMPLETED BEFORE LAUNCH.
        </p>

        <div className="gold-divider mx-auto mt-10" />

        <div
          data-testid="charter-cities"
          className="mt-12 flex flex-wrap justify-center gap-3 max-w-[820px] mx-auto"
        >
          {CHARTER_CITIES.map((city) => (
            <span key={city} className="jt-city-pill" data-testid={`charter-city-${city.replace(/\s+/g,'-').toLowerCase()}`}>
              {city}
            </span>
          ))}
        </div>

        <p
          className="font-editorial italic mt-12"
          style={{ color: "var(--jt-bone)", fontSize: "20px", maxWidth: "60ch", margin: "48px auto 0", lineHeight: 1.55 }}
        >
          Before a single public application was accepted, twenty-five flight attendants from major airlines and bases were invited to help shape the foundation of Jumpseat Therapy.
        </p>

        <p
          className="mt-6"
          style={{ color: "var(--jt-silver)", fontSize: "16px", maxWidth: "56ch", margin: "24px auto 0", lineHeight: 1.75 }}
        >
          Every Charter Member was selected individually. Every seat was filled before launch.
        </p>
      </div>
    </section>
  );
};

/* ---------------------------------- VERIFIED ---------------------------------- */

const VerifiedSection = () => {
  const [ref, shown] = useReveal();
  return (
    <section
      ref={ref}
      data-testid="verified-section"
      data-shown={shown}
      className="reveal relative py-24 px-6"
    >
      <div className="max-w-[860px] mx-auto text-center">
        <div
          className="font-label"
          style={{ color: "var(--jt-gold)", fontSize: "11px", marginBottom: 24 }}
        >
          VERIFIED ACCESS
        </div>

        <h2
          className="font-display"
          style={{
            fontSize: "clamp(2rem, 3.8vw, 3.2rem)",
            color: "var(--jt-ivory)",
            fontWeight: 500,
            lineHeight: 1.08,
            letterSpacing: "-0.005em",
            maxWidth: "20ch",
            margin: "0 auto",
          }}
        >
          <span className="font-editorial italic" style={{ color: "var(--jt-bone)" }}>Every member</span>
          <br />
          is verified.
        </h2>

        <div className="gold-divider mx-auto mt-10" />

        <p
          className="font-editorial italic mt-10"
          style={{
            color: "var(--jt-bone)",
            fontSize: "clamp(1.2rem, 1.8vw, 1.55rem)",
            lineHeight: 1.55,
            maxWidth: "44ch",
            margin: "0 auto",
          }}
        >
          Entry requires verification before access is granted.
        </p>

        <p
          className="mt-6"
          style={{
            color: "var(--jt-silver)",
            fontSize: "16px",
            lineHeight: 1.75,
            maxWidth: "44ch",
            margin: "24px auto 0",
          }}
        >
          The lounge is reserved exclusively for flight attendants.
        </p>

        <div
          className="gold-divider mx-auto"
          style={{ marginTop: 48, marginBottom: 40 }}
        />

        <p
          data-testid="verified-privacy-note"
          className="font-editorial italic"
          style={{
            color: "var(--jt-bone)",
            fontSize: "17px",
            lineHeight: 1.7,
            maxWidth: "56ch",
            margin: "0 auto",
          }}
        >
          Your privacy comes first. Jumpseat Therapy will never ask you to upload employee badges,
          employee ID numbers, company login credentials, or other sensitive employment information.
        </p>

        <p
          className="mt-6"
          style={{
            color: "var(--jt-silver)",
            fontSize: "15px",
            lineHeight: 1.75,
            maxWidth: "56ch",
            margin: "24px auto 0",
          }}
        >
          Employment verification is completed through a brief Concierge Verification process
          designed to protect both your privacy and your employer's policies.
        </p>
      </div>
    </section>
  );
};

/* ---------------------------------- WHY WE BUILT THIS ---------------------------------- */

const WhyWeBuiltThis = () => {
  const [ref, shown] = useReveal();
  return (
    <section
      ref={ref}
      data-testid="why-section"
      data-shown={shown}
      className="reveal relative py-24 px-6 lounge-soft"
    >
      <div className="max-w-[820px] mx-auto text-center">
        <div
          className="font-label"
          style={{ color: "var(--jt-gold)", fontSize: "11px", marginBottom: 24 }}
        >
          WHY WE BUILT THIS
        </div>

        <h2
          className="font-display"
          style={{
            fontSize: "clamp(1.85rem, 3.2vw, 2.6rem)",
            color: "var(--jt-ivory)",
            fontWeight: 500,
            lineHeight: 1.22,
            letterSpacing: "0.005em",
            maxWidth: "32ch",
            margin: "0 auto",
          }}
        >
          <span className="font-editorial italic" style={{ color: "var(--jt-bone)" }}>
            Because some of the hardest parts of this career happen after the aircraft door closes.
          </span>
        </h2>

        <div className="gold-divider mx-auto mt-12" />

        <div className="mt-12 space-y-7 text-left sm:text-center" style={{ maxWidth: "60ch", margin: "48px auto 0" }}>
          <p
            style={{
              color: "var(--jt-silver)",
              fontSize: "17px",
              lineHeight: 1.8,
            }}
          >
            Flight attendants miss holidays, birthdays, anniversaries, graduations, and moments with family that can never be replaced. Constant travel, changing schedules, and life between cities can create isolation, loneliness, and a quiet decline in mental well-being.
          </p>

          <p
            className="font-editorial italic"
            style={{
              color: "var(--jt-bone)",
              fontSize: "19px",
              lineHeight: 1.55,
            }}
          >
            Over time, too many layovers become another hotel room and another closed door.
          </p>

          <p
            className="font-editorial italic"
            style={{
              color: "var(--jt-bone)",
              fontSize: "20px",
              lineHeight: 1.55,
            }}
          >
            Jumpseat Therapy was created to bring flight attendants back into the cities they fly to — and back to each other.
          </p>

          <p
            className="font-editorial italic"
            style={{
              color: "var(--jt-gold)",
              fontSize: "19px",
              lineHeight: 1.55,
            }}
          >
            Because no one should have to navigate this career alone.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ---------------------------------- WHY MEMBERS JOIN ---------------------------------- */

const WhyMembersJoin = () => {
  const [ref, shown] = useReveal();
  const reasons = [
    "To experience the city again.",
    "To meet other flight attendants.",
    "To stop spending every layover alone.",
    "To discover places worth leaving the hotel for.",
    "To build friendships across airlines and bases.",
    "To find familiar faces in unfamiliar cities.",
    "To belong somewhere that understands the lifestyle.",
  ];

  return (
    <section
      ref={ref}
      data-testid="why-members-join-section"
      data-shown={shown}
      className="reveal relative pt-24 pb-12 sm:pb-16 px-6 lounge-soft"
    >
      <div className="max-w-[900px] mx-auto text-center">
        <div
          className="font-label"
          style={{ color: "var(--jt-gold)", fontSize: "11px", marginBottom: 24 }}
        >
          WHY MEMBERS JOIN
        </div>

        <h2
          className="font-display"
          style={{
            fontSize: "clamp(2rem, 4vw, 3.4rem)",
            color: "var(--jt-ivory)",
            fontWeight: 500,
            lineHeight: 1.08,
            letterSpacing: "-0.005em",
            maxWidth: "16ch",
            margin: "0 auto",
          }}
        >
          <span className="font-editorial italic" style={{ color: "var(--jt-bone)" }}>
            Not for another app.
          </span>
          <br />
          For the people.
        </h2>

        <div className="gold-divider mx-auto mt-12" />

        <ul
          data-testid="why-members-list"
          className="mt-14 flex flex-col items-center"
          style={{ maxWidth: "44ch", margin: "56px auto 0", listStyle: "none", padding: 0 }}
        >
          {reasons.map((line, i) => (
            <li
              key={i}
              data-testid={`why-members-reason-${i + 1}`}
              className="font-editorial italic"
              style={{
                color: "var(--jt-bone)",
                fontSize: "clamp(1.2rem, 1.9vw, 1.55rem)",
                lineHeight: 1.5,
                padding: "22px 0",
                borderBottom: i < reasons.length - 1 ? "1px solid rgba(201,162,75,0.12)" : "none",
                width: "100%",
              }}
            >
              {line}
            </li>
          ))}
        </ul>

        <div className="gold-divider mx-auto mt-16" />

        <p
          className="font-editorial italic mt-12"
          data-testid="why-members-closing"
          style={{
            color: "var(--jt-gold)",
            fontSize: "clamp(1.35rem, 2.4vw, 2rem)",
            lineHeight: 1.45,
            maxWidth: "30ch",
            margin: "48px auto 0",
          }}
        >
          {`"Because sometimes one conversation can change an entire layover."`}
        </p>
      </div>
    </section>
  );
};

/* ----------------------------- CLOSING INVITATION ----------------------------- */

const ClosingInvitation = () => {
  const [ref, shown] = useReveal();
  return (
    <section
      ref={ref}
      data-testid="closing-section"
      data-shown={shown}
      className="reveal relative pt-12 sm:pt-16 pb-24 px-6"
    >
      <div className="max-w-[820px] mx-auto text-center">
        <div className="gold-divider mx-auto mb-10" />

        <div
          className="font-label"
          style={{ color: "var(--jt-gold)", fontSize: "11px", marginBottom: 28 }}
        >
          MEMBERSHIP REQUESTS NOW OPEN
        </div>

        <p
          className="font-editorial italic"
          style={{
            fontSize: "clamp(1.4rem, 2.4vw, 2.1rem)",
            color: "var(--jt-bone)",
            lineHeight: 1.45,
            maxWidth: "30ch",
            margin: "0 auto",
          }}
        >
          A private members community built exclusively for verified flight attendants.
        </p>

        <p
          className="mt-5"
          style={{ color: "var(--jt-silver)", fontSize: "14px", lineHeight: 1.65, letterSpacing: "0.01em" }}
        >
          Access is not guaranteed.
        </p>

        <p
          className="mt-8"
          style={{ color: "var(--jt-silver)", fontSize: "15px", lineHeight: 1.7 }}
        >
          Every application is reviewed by hand.
        </p>

        <div className="gold-divider mx-auto mt-16" />

        <div
          className="mt-14 sm:mt-16"
          data-testid="closing-bridge"
        >
          <p
            className="font-display"
            style={{
              color: "var(--jt-ivory)",
              fontSize: "clamp(1.7rem, 3.2vw, 2.6rem)",
              lineHeight: 1.18,
              fontWeight: 500,
              letterSpacing: "-0.005em",
            }}
          >
            The Charter Class built it.
          </p>
          <p
            className="font-editorial italic mt-4"
            style={{
              color: "var(--jt-gold)",
              fontSize: "clamp(1.7rem, 3.2vw, 2.6rem)",
              lineHeight: 1.22,
            }}
          >
            The Founding Class will shape what comes next.
          </p>
        </div>

        <div
          className="mt-16 sm:mt-20"
          data-testid="founding-class-2026"
        >
          <div
            className="font-label"
            style={{ color: "var(--jt-gold)", fontSize: "11px", letterSpacing: "0.42em", marginBottom: 18 }}
          >
            FOUNDING CLASS 2026
          </div>
          <p
            className="font-editorial italic"
            style={{
              color: "var(--jt-bone)",
              fontSize: "clamp(1.15rem, 1.8vw, 1.5rem)",
              lineHeight: 1.5,
              maxWidth: "40ch",
              margin: "0 auto",
            }}
          >
            Applications are being accepted for a limited number of Founding Class positions.
          </p>
        </div>

        <Link to="/apply" className="jt-cta mt-10" data-testid="closing-cta">
          Request Membership
        </Link>
      </div>
    </section>
  );
};
